import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseSiteService, OccEndpointsService } from '@spartacus/core';
import { BehaviorSubject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import * as config from '../shared/config/app.config';
import { BaseService } from '../shared/services/base.service';
import { ExcelDownloadService } from '../shared/services/excel-download.service';

export type PaymentHistoryExportKind = 'legacyExport' | 'export';

@Injectable({
  providedIn: 'root'
})
export class PaymentHistoryService {

  public userData = new BehaviorSubject<any>(null);
  userData$ = this.userData.asObservable();
  public detailsSummary = new BehaviorSubject<any>(null);
  detailsSummary$ = this.detailsSummary.asObservable();
  public legacyPayment = new BehaviorSubject<any>(null);
  legacyPayment$ = this.legacyPayment.asObservable();
  public transactionalData = new BehaviorSubject<any>(null);
  transactionalData$ = this.transactionalData.asObservable();

  constructor(
    public baseService: BaseService,
    public excelService: ExcelDownloadService,
    private readonly http: HttpClient,
    private readonly occEndPoint: OccEndpointsService,
    private readonly baseSiteService: BaseSiteService
  ) { }

  public getPaymentHistory(params, filters?) {
    this.baseService
      .get(`/my-account/${params.userId}/paymenthistory`, filters)
      .subscribe((res) => {
        this.userData.next(res);
      });
  }

  public getLegacyPaymentHistory(params, filters) {
    this.baseService
      .get(`/my-account/${params.userId}/legacyPaymenthistory`, filters)
      .subscribe((res) => {
        this.legacyPayment.next(res);
      });
  }

  public getPieChartDetails(params, filters?) {
    return this.baseService
      .get(`/my-account/${params.userId}/paymentsummary`, filters)
  }

  public downloadXls(data, name) {
    this.excelService.exportAsExcelFile(data, name, true);
  }
  
  downloadPaymentHistoryXlsOrPdf(
    params: { userId: string },
    format: 'excel' | 'pdf',
    filters: Record<string, unknown> | undefined,
    exportKind: PaymentHistoryExportKind
  ): void {
    let query = '';
    if (filters) {
      for (const prop in filters) {
        if (Object.prototype.hasOwnProperty.call(filters, prop)) {
          query += `${prop}=${filters[prop]}&`;
        }
      }
      query = query.length > 0 ? query.substring(0, query.length - 1) : '';
    }
    const baseUrl = this.occEndPoint.getBaseUrl({
      baseUrl: true,
      prefix: false,
      baseSite: false,
    });
    const segment = format === 'excel' ? 'excel' : 'pdf';
    this.baseSiteService
      .getActive()
      .pipe(
        take(1),
        switchMap((baseSiteId) => {
          const url = `${baseUrl}/sanofiusocc/v2/${baseSiteId}/my-account/${params.userId}/${exportKind}/${segment}?${query}`;
          return this.http.get(url, { responseType: 'blob', observe: 'response' });
        })
      )
      .subscribe((response) => {
        const blob = response.body;
        if (!blob) {
          return;
        }
        const name =
          response.headers.get('Content-Disposition')?.match(/filename="?([^";\n]+)"?/)?.[1] ??
          (format === 'excel' ? 'PaymentHistory.xls' : 'PaymentHistory.pdf');
        const href = URL.createObjectURL(blob);
        if (format === 'pdf') {
          window.open(href, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = href;
          a.download = name;
          a.click();
        }
        URL.revokeObjectURL(href);
      });
  }

  public downloadPdf(params): void {
    this.baseService
      .getPdf(config.DOWNLOAD_INVOICE_CREDIT_PDF, { invoiceNumber: params })
      .subscribe((res) => {
        const file = new Blob([res], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      });
  }

  getInvoicepdf(params) {
    this.baseService
      .getPdf(config.DOWNLOAD_INVOICE_PDF, { invoiceNumber: params })
      .subscribe((res) => {
        const file = new Blob([res], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      });
  }

  getTransactionalDetails(params, filters) {
    return this.baseService
      .get(`/my-account/${params.userId}/legacyTransactional-data`, filters)
  }

}
